/*
 * SPDX-License-Identifier: MIT
 * Copyright (c) 2024 Tech Inverge Solutions Pvt Ltd
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  Search, 
  RefreshCw, 
  Eye, 
  Ban, 
  UserCheck, 
  X, 
  Loader,
  Calendar,
  ShoppingBag
} from 'lucide-react';
import { buildApiUrl } from '../config';

interface UserAddress {
  street: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

interface User {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  address?: UserAddress;
  isBlocked?: boolean;
  createdAt: string;
}

interface OrderItem {
  productId: {
    title: string;
  } | null;
  quantity: number;
  priceAtPurchase: number;
  selectedColor?: string;
}

interface UserOrder {
  _id: string;
  razorpayOrderId?: string;
  paymentMethod?: 'Prepaid' | 'COD';
  amount: number;
  status: 'created' | 'placed' | 'paid' | 'failed' | 'cod_pending';
  items: OrderItem[];
  createdAt: string;
}

const UserManagement: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const itemsPerPage = 20;

  // Modals state
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userOrders, setUserOrders] = useState<UserOrder[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [showOrdersModal, setShowOrdersModal] = useState(false);
  const [isUpdatingBlockStatus, setIsUpdatingBlockStatus] = useState(false);

  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      throw new Error('No token found');
    }

    const headers = {
      ...options.headers,
      'Authorization': `Bearer ${token}`
    } as HeadersInit;

    const response = await fetch(url, { ...options, headers });

    if (response.status === 401) {
      localStorage.removeItem('admin_token');
      navigate('/admin/login');
      throw new Error('Session expired');
    }

    return response;
  };

  useEffect(() => {
    fetchUsers();
  }, [currentPage]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
      });

      if (searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }

      const response = await authenticatedFetch(
        buildApiUrl(`/api/admin/users?${params}`)
      );

      const data = await response.json();
      if (data.success) {
        setUsers(data.users || []);
        setTotal(data.pagination.total || 0);
        setTotalPages(data.pagination.totalPages || 1);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchUsers();
  };

  const handleToggleBlock = async (user: User) => {
    const actionText = user.isBlocked ? 'unblock' : 'block/suspend';
    if (!window.confirm(`Are you sure you want to ${actionText} customer ${user.firstName} ${user.lastName}?`)) {
      return;
    }

    setIsUpdatingBlockStatus(true);
    try {
      const response = await authenticatedFetch(
        buildApiUrl(`/api/admin/users/${user._id}/block`),
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ isBlocked: !user.isBlocked }),
        }
      );

      const data = await response.json();
      if (response.ok && data.success) {
        alert(data.message);
        // Refresh local users listing
        fetchUsers();
      } else {
        alert(data.message || 'Failed to update user block status');
      }
    } catch (error) {
      console.error('Toggle block status error:', error);
      alert('An error occurred while updating user block status');
    } finally {
      setIsUpdatingBlockStatus(false);
    }
  };

  const viewUserOrders = async (user: User) => {
    setSelectedUser(user);
    setShowOrdersModal(true);
    setLoadingOrders(true);
    try {
      const response = await authenticatedFetch(
        buildApiUrl(`/api/admin/users/${user._id}/orders`)
      );
      const data = await response.json();
      if (data.success) {
        setUserOrders(data.orders || []);
      }
    } catch (error) {
      console.error('Failed to load user orders:', error);
    } finally {
      setLoadingOrders(false);
    }
  };

  const getOrderStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 border-green-200 text-green-800';
      case 'placed':
        return 'bg-blue-100 border-blue-200 text-blue-800';
      case 'created':
      case 'cod_pending':
        return 'bg-yellow-100 border-yellow-200 text-yellow-800';
      case 'failed':
        return 'bg-red-100 border-red-200 text-red-800';
      default:
        return 'bg-gray-100 border-gray-200 text-gray-800';
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-8">
      
      {/* Title Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between pb-5 border-b border-gray-200">
        <div>
          <div className="flex items-center space-x-3">
            <Users className="w-7 h-7 text-pink-600" />
            <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Registered Customers</h1>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Browse registered customer profiles, view order logs, or manage status blocks
          </p>
        </div>
        <div className="flex items-center space-x-3 mt-4 md:mt-0">
          <span className="text-xs font-semibold text-gray-700 bg-white border border-gray-200 px-3 py-1.5 rounded-lg shadow-sm">
            Total Customers: {total}
          </span>
          <button
            onClick={fetchUsers}
            className="p-2 bg-white hover:bg-gray-100 border border-gray-200 text-gray-700 rounded-lg shadow-sm transition-colors"
            title="Refresh List"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Filter and Search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <form onSubmit={handleSearchSubmit} className="max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search customers by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-24 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm"
            />
            <button
              type="submit"
              className="absolute right-1.5 top-1.5 px-3 py-1 bg-gray-800 text-white rounded-md hover:bg-gray-900 transition-colors text-xs font-semibold"
            >
              Search
            </button>
          </div>
        </form>
      </div>

      {/* Customers Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading && users.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader className="w-10 h-10 text-pink-600 animate-spin mb-4" />
            <p className="text-gray-500 text-sm">Loading registered customers...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-20">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900">No Customers Found</h3>
            <p className="text-gray-500 mt-1 max-w-sm mx-auto">
              No registered profiles match your current search terms. Try clearing search input.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left">
              <thead>
                <tr className="bg-gray-50 text-gray-500 border-b border-gray-200 text-xs font-semibold uppercase tracking-wider">
                  <th className="px-6 py-4">Customer Details</th>
                  <th className="px-6 py-4">Joined Date</th>
                  <th className="px-6 py-4">Phone Number</th>
                  <th className="px-6 py-4">Default Address</th>
                  <th className="px-6 py-4">Block Status</th>
                  <th className="px-6 py-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 text-sm text-gray-700">
                {users.map((user) => {
                  const joinedDate = new Date(user.createdAt).toLocaleDateString('en-IN', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric'
                  });

                  return (
                    <tr key={user._id} className={`hover:bg-gray-50/70 transition-colors ${user.isBlocked ? 'bg-red-50/10' : ''}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3.5">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm border ${
                            user.isBlocked 
                              ? 'bg-red-50 text-red-600 border-red-100 shadow-sm shadow-red-50/10'
                              : 'bg-pink-50 text-pink-600 border-pink-100 shadow-sm shadow-pink-50/10'
                          }`}>
                            {user.firstName[0]}{user.lastName[0]}
                          </div>
                          <div>
                            <div className="font-bold text-gray-900 text-sm">{user.firstName} {user.lastName}</div>
                            <div className="text-xs text-gray-400 mt-0.5">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-500 text-xs">
                        <div className="flex items-center space-x-1.5">
                          <Calendar className="w-3.5 h-3.5 text-gray-300" />
                          <span>{joinedDate}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-900 text-xs font-semibold">
                        {user.phone || 'N/A'}
                      </td>
                      <td className="px-6 py-4 max-w-xs text-xs text-gray-500 leading-relaxed">
                        {user.address ? (
                          <div className="truncate" title={`${user.address.street}, ${user.address.city}, ${user.address.state}`}>
                            {user.address.street}, {user.address.city}, {user.address.state}
                          </div>
                        ) : (
                          <span className="italic text-gray-400">No Address Saved</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.isBlocked ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-800 border border-red-200">
                            Blocked / Suspended
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-800 border border-green-200">
                            Active Account
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center space-x-2">
                          <button
                            onClick={() => viewUserOrders(user)}
                            className="p-2 bg-blue-50 hover:bg-blue-100 border border-blue-200 text-blue-600 rounded-lg shadow-sm transition-all"
                            title="View Orders History"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleToggleBlock(user)}
                            disabled={isUpdatingBlockStatus}
                            className={`p-2 border rounded-lg shadow-sm transition-all ${
                              user.isBlocked
                                ? 'bg-green-50 hover:bg-green-100 border-green-200 text-green-600'
                                : 'bg-red-50 hover:bg-red-100 border-red-200 text-red-600'
                            }`}
                            title={user.isBlocked ? 'Activate User' : 'Suspend User'}
                          >
                            {user.isBlocked ? <UserCheck className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Footer */}
        {totalPages > 1 && (
          <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-between">
            <span className="text-xs text-gray-500">
              Showing Page {currentPage} of {totalPages} (Total {total} customers)
            </span>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 bg-white border border-gray-300 rounded-md text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                    currentPage === page
                      ? 'bg-pink-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 bg-white border border-gray-300 rounded-md text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* User Orders History Modal */}
      {showOrdersModal && selectedUser && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl border border-gray-200 w-full max-w-3xl overflow-hidden max-h-[90vh] flex flex-col animate-in fade-in zoom-in-95 duration-150">
            
            {/* Modal Header */}
            <div className="flex items-center justify-between bg-gray-50 px-6 py-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-bold text-gray-900 flex items-center">
                  <ShoppingBag className="w-5 h-5 text-indigo-600 mr-2" />
                  Order History: {selectedUser.firstName} {selectedUser.lastName}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Email: {selectedUser.email}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowOrdersModal(false);
                  setSelectedUser(null);
                  setUserOrders([]);
                }}
                className="p-1.5 bg-white hover:bg-gray-100 border border-gray-200 text-gray-500 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-grow overflow-y-auto p-6">
              
              {loadingOrders ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <Loader className="w-8 h-8 text-indigo-600 animate-spin mb-4" />
                  <p className="text-gray-500 text-sm">Fetching user order logs...</p>
                </div>
              ) : userOrders.length === 0 ? (
                <div className="text-center py-16 bg-gray-50/50 rounded-xl border border-dashed border-gray-200">
                  <ShoppingBag className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <h4 className="font-semibold text-gray-900">No Orders Registered</h4>
                  <p className="text-xs text-gray-500 mt-1">This user has not placed any orders yet.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {userOrders.map((order) => {
                    const date = new Date(order.createdAt).toLocaleDateString('en-IN', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric'
                    });

                    return (
                      <div key={order._id} className="border border-gray-200 rounded-xl overflow-hidden shadow-sm bg-white hover:shadow-md transition-shadow">
                        
                        {/* Order Header summary */}
                        <div className="bg-gray-50 px-4 py-3 border-b border-gray-150 flex items-center justify-between text-xs font-semibold">
                          <div>
                            <span className="text-gray-500">Order ID:</span>{' '}
                            <span className="text-gray-900 font-bold">{order.razorpayOrderId || order._id}</span>
                            <span className="mx-2 text-gray-300">|</span>
                            <span className="text-gray-500">Date:</span>{' '}
                            <span className="text-gray-800">{date}</span>
                          </div>
                          <span className={`inline-flex items-center px-2 py-0.5 border rounded-full text-[10px] font-bold capitalize ${getOrderStatusBadge(order.status)}`}>
                            {order.status}
                          </span>
                        </div>

                        {/* Order Items Table */}
                        <div className="px-4 py-3 divide-y divide-gray-100 text-xs">
                          {order.items.map((item, idx) => (
                            <div key={idx} className="py-2.5 flex items-center justify-between first:pt-1 last:pb-1">
                              <div>
                                <span className="font-bold text-gray-900 block leading-tight">{item.productId ? item.productId.title : 'Deleted Product'}</span>
                                <div className="flex items-center space-x-2 mt-0.5">
                                  <span className="text-gray-400">Quantity: {item.quantity}</span>
                                  {item.selectedColor && (
                                    <span className="text-[10px] font-bold text-indigo-700 bg-indigo-50 border border-indigo-100 px-1.5 py-0.2 rounded">
                                      Color: {item.selectedColor}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <span className="font-semibold text-gray-700">₹{item.priceAtPurchase.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>

                        {/* Order Total amount */}
                        <div className="bg-gray-50/50 px-4 py-2 border-t border-gray-100 flex items-center justify-between text-xs">
                          <span className="text-gray-500 font-medium">Order Total</span>
                          <span className="font-extrabold text-blue-600 text-sm">₹{order.amount.toFixed(2)}</span>
                        </div>

                      </div>
                    );
                  })}
                </div>
              )}

            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => {
                  setShowOrdersModal(false);
                  setSelectedUser(null);
                  setUserOrders([]);
                }}
                className="px-4 py-2 bg-white hover:bg-gray-100 border border-gray-300 rounded-lg text-xs font-semibold text-gray-700 transition-colors"
              >
                Close Logs
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};

export default UserManagement;
